import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Eye, Download, CheckCircle, 
  XCircle, User, FileText, Shield,
  AlertCircle, Globe, Calendar, 
  CreditCard, Mail, Phone, MapPin,
  Image, Home, Copy, ExternalLink,
  Clock, BarChart3, Wallet, Users,
  TrendingUp, TrendingDown, Activity,
  Filter, Search, RefreshCw, MoreVertical,
  ShieldCheck, KeyRound, Settings, LogOut,
  ChevronDown, ChevronRight, ChevronLeft,
  Upload, ZoomIn, FileCheck
} from 'lucide-react';
import { AdminKYCAPI, AdminAPIUtils } from '../services/api';

function KYCDetail() {
  const { kycId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verificationForm, setVerificationForm] = useState({
    status: 'verified',
    rejectionReason: ''
  });
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [documents, setDocuments] = useState([]);
  const [showImageModal, setShowImageModal] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSubmission();
    fetchStats();
  }, [kycId]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      const data = await AdminKYCAPI.getKYCSubmission(kycId);
      setSubmission(data);
      setUserInfo(data.user_info);
      
      // Set up documents array
      const docs = [];
      if (data.document_front) docs.push({ 
        type: 'document_front', 
        label: 'Document Front', 
        icon: <FileText className="w-5 h-5" />,
        color: 'from-blue-500 to-cyan-500'
      });
      if (data.document_back) docs.push({ 
        type: 'document_back', 
        label: 'Document Back', 
        icon: <FileText className="w-5 h-5" />,
        color: 'from-indigo-500 to-blue-500'
      });
      if (data.selfie_with_document) docs.push({ 
        type: 'selfie_with_document', 
        label: 'Selfie with Document', 
        icon: <Image className="w-5 h-5" />,
        color: 'from-emerald-500 to-green-500'
      });
      if (data.proof_of_address) docs.push({ 
        type: 'proof_of_address', 
        label: 'Proof of Address', 
        icon: <Home className="w-5 h-5" />,
        color: 'from-amber-500 to-orange-500'
      });
      setDocuments(docs);
    } catch (err) {
      const errorMsg = AdminAPIUtils.handleError(err, false).message;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await AdminKYCAPI.getKYCStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch KYC stats:', err);
    }
  };

  const verifyKYC = async (status, rejectionReason = '') => {
    try {
      setVerificationLoading(true);
      await AdminKYCAPI.verifyKYC(kycId, status, rejectionReason);
      alert(`KYC ${status === 1 ? 'verified' : 'rejected'} successfully`);
      fetchSubmission();
      setActiveTab('overview');
    } catch (err) {
      AdminAPIUtils.handleError(err);
    } finally {
      setVerificationLoading(false);
    }
  };

  const downloadDocument = async (documentType) => {
    try {
      await AdminKYCAPI.downloadDocument(kycId, documentType);
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 0) {
      return (
        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          Pending Review
        </span>
      );
    } else if (status === 1) {
      return (
        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
          <CheckCircle className="w-3 h-3" />
          Verified
        </span>
      );
    } else if (status === 2) {
      return (
        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center gap-1.5">
          <XCircle className="w-3 h-3" />
          Rejected
        </span>
      );
    }
    return (
      <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-500/10 text-gray-400 border border-gray-500/20">
        Unknown
      </span>
    );
  };

  const getDocumentTypeIcon = (type) => {
    const icons = {
      'passport': <Globe className="w-5 h-5" />,
      'id_card': <CreditCard className="w-5 h-5" />,
      'drivers_license': <CreditCard className="w-5 h-5" />,
      'residence_permit': <Home className="w-5 h-5" />
    };
    
    return icons[type] || <FileText className="w-5 h-5" />;
  };

  const getDocumentTypeBadge = (type) => {
    const colors = {
      'passport': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      'id_card': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'drivers_license': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      'residence_permit': 'bg-amber-500/10 text-amber-400 border-amber-500/20'
    };
    
    const labels = {
      'passport': 'Passport',
      'id_card': 'National ID Card',
      'drivers_license': 'Driver License',
      'residence_permit': 'Residence Permit'
    };
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${colors[type] || 'bg-gray-500/10 text-gray-400'} border flex items-center gap-1.5`}>
        {getDocumentTypeIcon(type)}
        {labels[type] || type}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return AdminAPIUtils.formatDate(dateString, 'datetime');
  };

  const handleVerificationSubmit = async () => {
    if (verificationForm.status === 'rejected' && !verificationForm.rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    const confirmMessage = verificationForm.status === 'verified' 
      ? 'Are you sure you want to verify this KYC submission?'
      : 'Are you sure you want to reject this KYC submission?';
    
    if (!window.confirm(confirmMessage)) return;

    await verifyKYC(
      verificationForm.status === 'verified' ? 1 : 2,
      verificationForm.rejectionReason
    );
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const statCards = [
    {
      title: 'Pending Reviews',
      value: stats?.pending_count || 0,
      icon: <Clock className="w-5 h-5" />,
      color: 'from-amber-500 to-orange-500',
      change: 'Needs attention',
      changeText: 'awaiting review',
      trend: 'neutral'
    },
    {
      title: 'Verified Today',
      value: stats?.verified_today || 0,
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      change: '24h',
      changeText: 'approvals',
      trend: 'up'
    },
    {
      title: 'Rejection Rate',
      value: stats?.rejection_rate ? `${stats.rejection_rate}%` : '0%',
      icon: <XCircle className="w-5 h-5" />,
      color: 'from-rose-500 to-red-500',
      change: 'All time',
      changeText: 'of submissions',
      trend: 'neutral'
    },
    {
      title: 'Avg. Review Time',
      value: stats?.avg_review_time ? `${stats.avg_review_time}h` : '-',
      icon: <Activity className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-500',
      change: 'Efficiency',
      changeText: 'processing speed',
      trend: 'neutral'
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading KYC details...</p>
        </div>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center max-w-md">
          <AlertCircle className="w-20 h-20 text-rose-400 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">
            {error ? 'Error Loading KYC' : 'KYC Not Found'}
          </h2>
          <p className="text-charcoal-gray mb-8">
            {error || 'The KYC submission you are looking for does not exist.'}
          </p>
          <button
            onClick={() => navigate('/kyc')}
            className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
          >
            Back to KYC List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/kyc')}
            className="p-2.5 bg-night-blue/50 hover:bg-night-blue border border-gray-800 text-white rounded-xl transition-all duration-300 hover:scale-105"
            title="Back to KYC"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
              KYC Verification
            </h1>
            <p className="text-charcoal-gray mt-1">Review and verify identity documents</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {submission.status === 0 && (
            <>
              <button
                onClick={() => {
                  setVerificationForm({ status: 'verified', rejectionReason: '' });
                  setActiveTab('verify');
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
              >
                <CheckCircle className="w-4 h-4" />
                Verify
              </button>
              <button
                onClick={() => {
                  setVerificationForm({ status: 'rejected', rejectionReason: '' });
                  setActiveTab('verify');
                }}
                className="px-4 py-2.5 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </button>
            </>
          )}
          <button
            onClick={fetchSubmission}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">Active</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className="font-medium text-gray-300">{card.change}</span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Submission Header */}
      <div className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="relative">
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
              {submission.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-night-blue ${
              submission.status === 1 ? 'bg-emerald-500' : 
              submission.status === 2 ? 'bg-rose-500' : 
              'bg-amber-500'
            }`} />
          </div>
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">KYC #{submission.id}</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-charcoal-gray">{submission.username}</div>
                  {getStatusBadge(submission.status)}
                  {getDocumentTypeBadge(submission.document_type)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(submission.document_number);
                    alert('Document number copied to clipboard!');
                  }}
                  className="p-2.5 bg-gray-800/50 hover:bg-sky-500/20 text-sky-400 hover:text-sky-300 rounded-xl transition-all duration-300 hover:scale-110"
                  title="Copy Document Number"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigate(`/users/${submission.user_id}`)}
                  className="p-2.5 bg-gray-800/50 hover:bg-purple-500/20 text-purple-400 hover:text-purple-300 rounded-xl transition-all duration-300 hover:scale-110"
                  title="View User Profile"
                >
                  <ExternalLink className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-charcoal-gray" />
                <div>
                  <p className="text-sm text-charcoal-gray">Full Name</p>
                  <p className="text-white font-medium">{submission.full_name}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-charcoal-gray" />
                <div>
                  <p className="text-sm text-charcoal-gray">Date of Birth</p>
                  <p className="text-white font-medium">
                    {submission.date_of_birth ? formatDate(submission.date_of_birth) : 'Not provided'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-charcoal-gray" />
                <div>
                  <p className="text-sm text-charcoal-gray">Nationality</p>
                  <p className="text-white font-medium">{submission.nationality || 'Not provided'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-charcoal-gray" />
                <div>
                  <p className="text-sm text-charcoal-gray">Submitted</p>
                  <p className="text-white font-medium">
                    {AdminAPIUtils.timeAgo(submission.submitted_at)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Tabs */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs Navigation */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl">
            <div className="border-b border-gray-800 px-6">
              <nav className="flex -mb-px overflow-x-auto">
                {[
                  { id: 'overview', label: 'Overview', icon: <Eye className="w-4 h-4" /> },
                  { id: 'documents', label: 'Documents', icon: <FileText className="w-4 h-4" /> },
                  { id: 'user', label: 'User Profile', icon: <User className="w-4 h-4" /> },
                  { id: 'verify', label: 'Verification', icon: <ShieldCheck className="w-4 h-4" />, badge: submission.status === 0 ? 'Pending' : null }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-6 font-medium text-sm border-b-2 whitespace-nowrap transition-all duration-300 flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'border-sky-500 text-sky-400'
                        : 'border-transparent text-charcoal-gray hover:text-gray-300 hover:border-gray-600'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                    {tab.badge && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded-full">
                        {tab.badge}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  {/* Status & Timeline */}
                  <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
                    <h3 className="text-lg font-semibold text-white mb-6">Verification Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Current Status</label>
                        {getStatusBadge(submission.status)}
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Submitted</label>
                        <div className="text-white font-medium">
                          {formatDate(submission.submitted_at)}
                        </div>
                        <div className="text-sm text-charcoal-gray">
                          {AdminAPIUtils.timeAgo(submission.submitted_at)}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">
                          {submission.status === 0 ? 'Pending Since' : submission.status === 1 ? 'Verified On' : 'Rejected On'}
                        </label>
                        <div className="text-white font-medium">
                          {submission.verified_at ? formatDate(submission.verified_at) : 'Not yet'}
                        </div>
                      </div>
                    </div>
                    {submission.rejection_reason && (
                      <div className="mt-6 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl">
                        <h4 className="font-medium text-rose-400 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Rejection Reason
                        </h4>
                        <p className="text-rose-300/80">{submission.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  {/* Document Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-6">Document Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Document Type</label>
                        <div className="flex items-center gap-3 text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          {getDocumentTypeIcon(submission.document_type)}
                          <span className="font-medium">{getDocumentTypeBadge(submission.document_type).props.children[2]}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Document Number</label>
                        <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          <span className="font-mono text-white">{submission.document_number}</span>
                          <button
                            onClick={() => copyToClipboard(submission.document_number)}
                            className="text-charcoal-gray hover:text-white transition-colors"
                            title="Copy"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Issuing Country</label>
                        <div className="flex items-center gap-3 text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          <Globe className="w-5 h-5 text-charcoal-gray" />
                          {submission.document_country}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Expiry Date</label>
                        <div className="flex items-center gap-3 text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          <Calendar className="w-5 h-5 text-charcoal-gray" />
                          {formatDate(submission.document_expiry_date)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-6">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Full Name</label>
                        <div className="text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          {submission.full_name}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Gender</label>
                        <div className="text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          {submission.gender || 'Not provided'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'documents' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-white">Document Files</h3>
                    <button
                      onClick={() => {
                        // Download all documents
                        documents.forEach(doc => {
                          if (submission[doc.type]) {
                            downloadDocument(doc.type);
                          }
                        });
                      }}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300"
                      disabled={documents.length === 0}
                    >
                      <Download className="w-4 h-4" />
                      Download All
                    </button>
                  </div>
                  {documents.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No documents found</h3>
                      <p className="text-charcoal-gray">No document files have been uploaded</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {documents.map((doc) => (
                        <div key={doc.type} className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 hover:bg-gray-900/70 transition-all duration-300">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className={`p-3 rounded-xl bg-gradient-to-br ${doc.color} shadow-lg`}>
                                {doc.icon}
                              </div>
                              <div>
                                <div className="font-medium text-white">{doc.label}</div>
                                <div className="text-sm text-charcoal-gray">
                                  {submission[doc.type] ? 'File available' : 'No file'}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {submission[doc.type] && (
                                <button
                                  onClick={() => setShowImageModal(doc.type)}
                                  className="p-2 bg-gray-800/50 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 rounded-lg transition-all duration-300 hover:scale-110"
                                  title="Preview"
                                >
                                  <ZoomIn className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => downloadDocument(doc.type)}
                                disabled={!submission[doc.type]}
                                className={`p-2 rounded-lg transition-all duration-300 hover:scale-110 ${
                                  submission[doc.type]
                                    ? 'bg-gray-800/50 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300'
                                    : 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                                }`}
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {doc.type === 'document_front' && submission.document_front_type && (
                            <div className="text-sm text-gray-500 mt-4">
                              <span className="text-gray-400">Format:</span>{' '}
                              <span className="text-white font-mono">{submission.document_front_type}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'user' && userInfo && (
                <div className="space-y-8">
                  {/* User Profile Header */}
                  <div className="flex items-center gap-6 p-6 bg-gray-900/50 rounded-2xl border border-gray-800">
                    <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                      {userInfo.username?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-white text-xl mb-2">{userInfo.username}</div>
                      <div className="text-charcoal-gray flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {userInfo.email}
                      </div>
                      <div className="text-sm text-gray-500 mt-2">User ID: {userInfo.id}</div>
                    </div>
                    <button
                      onClick={() => navigate(`/users/${userInfo.id}`)}
                      className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white rounded-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Profile
                    </button>
                  </div>

                  {/* User Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-6">User Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Full Name</label>
                        <div className="text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          {userInfo.full_name || 'Not provided'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Phone Number</label>
                        <div className="flex items-center gap-3 text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          <Phone className="w-5 h-5 text-charcoal-gray" />
                          {userInfo.phone_number || 'Not provided'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Country</label>
                        <div className="flex items-center gap-3 text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          <Globe className="w-5 h-5 text-charcoal-gray" />
                          {userInfo.country || 'Not provided'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Account Status</label>
                        <div className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                          userInfo.status === 1 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                          userInfo.status === 0 ? 'bg-gray-500/10 text-gray-400 border-gray-500/20' :
                          'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        } border flex items-center gap-1.5`}>
                          {userInfo.status === 1 ? 'Active' : userInfo.status === 0 ? 'Inactive' : 'Suspended'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Email Verified</label>
                        <div className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                          userInfo.email_verified ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                          'bg-rose-500/10 text-rose-400 border-rose-500/20'
                        } border flex items-center gap-1.5`}>
                          {userInfo.email_verified ? 'Verified' : 'Not Verified'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Account Created</label>
                        <div className="text-white p-3 bg-gray-900/50 rounded-xl border border-gray-800">
                          {formatDate(userInfo.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  {userInfo.balance !== undefined && (
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-6">Financial Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg">
                              <Wallet className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="text-sm text-charcoal-gray">Available Balance</div>
                              <div className="text-2xl font-bold text-white">
                                ${AdminAPIUtils.formatNumber(userInfo.balance || 0)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg">
                              <Shield className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="text-sm text-charcoal-gray">Locked Balance</div>
                              <div className="text-2xl font-bold text-white">
                                ${AdminAPIUtils.formatNumber(userInfo.locked_balance || 0)}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 shadow-lg">
                              <BarChart3 className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="text-sm text-charcoal-gray">Total Balance</div>
                              <div className="text-2xl font-bold text-white">
                                ${AdminAPIUtils.formatNumber((userInfo.balance || 0) + (userInfo.locked_balance || 0))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'verify' && submission.status === 0 && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-lg font-semibold text-white">Verify KYC Submission</h3>
                      <p className="text-charcoal-gray mt-1">Make a decision on this KYC submission</p>
                    </div>
                    <button
                      onClick={() => setActiveTab('overview')}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-400 mb-2">Verification Required</h4>
                        <p className="text-amber-300/80">
                          Please review all submitted documents and user information carefully before making a verification decision.
                          Once approved or rejected, this action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {/* Verification Options */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-4">Select Decision</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <button
                          onClick={() => setVerificationForm({ ...verificationForm, status: 'verified' })}
                          className={`p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center justify-center ${
                            verificationForm.status === 'verified'
                              ? 'border-emerald-500 bg-emerald-500/10 shadow-lg scale-[1.02]'
                              : 'border-gray-800 hover:bg-gray-900/50 hover:border-gray-700'
                          }`}
                        >
                          <div className={`p-4 rounded-xl mb-4 ${
                            verificationForm.status === 'verified'
                              ? 'bg-gradient-to-br from-emerald-500 to-green-500'
                              : 'bg-gray-800'
                          }`}>
                            <CheckCircle className="w-8 h-8 text-white" />
                          </div>
                          <span className={`text-xl font-semibold mb-2 ${
                            verificationForm.status === 'verified' ? 'text-emerald-400' : 'text-white'
                          }`}>
                            Verify
                          </span>
                          <span className="text-sm text-center text-charcoal-gray">
                            Approve this KYC submission and grant full access
                          </span>
                        </button>
                        <button
                          onClick={() => setVerificationForm({ ...verificationForm, status: 'rejected' })}
                          className={`p-6 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center justify-center ${
                            verificationForm.status === 'rejected'
                              ? 'border-rose-500 bg-rose-500/10 shadow-lg scale-[1.02]'
                              : 'border-gray-800 hover:bg-gray-900/50 hover:border-gray-700'
                          }`}
                        >
                          <div className={`p-4 rounded-xl mb-4 ${
                            verificationForm.status === 'rejected'
                              ? 'bg-gradient-to-br from-rose-500 to-red-500'
                              : 'bg-gray-800'
                          }`}>
                            <XCircle className="w-8 h-8 text-white" />
                          </div>
                          <span className={`text-xl font-semibold mb-2 ${
                            verificationForm.status === 'rejected' ? 'text-rose-400' : 'text-white'
                          }`}>
                            Reject
                          </span>
                          <span className="text-sm text-center text-charcoal-gray">
                            Decline this KYC submission and require resubmission
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Rejection Reason */}
                    {verificationForm.status === 'rejected' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          Rejection Reason <span className="text-rose-400">*</span>
                        </label>
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-1">
                          <textarea
                            placeholder="Please provide a clear and specific reason for rejection. This will be shown to the user."
                            value={verificationForm.rejectionReason}
                            onChange={(e) => setVerificationForm(prev => ({ ...prev, rejectionReason: e.target.value }))}
                            className="w-full px-4 py-3 bg-transparent border-0 text-white focus:outline-none focus:ring-0 resize-none"
                            rows="4"
                            required
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-3 text-sm text-charcoal-gray">
                          <AlertCircle className="w-4 h-4" />
                          <span>The user will see this reason when they check their KYC status</span>
                        </div>
                      </div>
                    )}

                    {/* Verification Summary */}
                    <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
                      <h4 className="font-medium text-white mb-3">Summary</h4>
                      <div className="text-sm text-gray-400">
                        You are about to <span className={`font-semibold ${
                          verificationForm.status === 'verified' ? 'text-emerald-400' : 'text-rose-400'
                        }`}>
                          {verificationForm.status === 'verified' ? 'verify' : 'reject'}
                        </span> KYC submission <span className="font-semibold text-white">#{submission.id}</span> for user{' '}
                        <span className="font-semibold text-white">{submission.username}</span>.
                        {verificationForm.status === 'verified' ? ' The user will gain full account access.' : ' The user will need to resubmit their documents.'}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
                      <button
                        onClick={() => setActiveTab('overview')}
                        className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                        disabled={verificationLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleVerificationSubmit}
                        disabled={verificationLoading || (verificationForm.status === 'rejected' && !verificationForm.rejectionReason.trim())}
                        className={`px-5 py-2.5 rounded-xl transition-all duration-300 flex items-center gap-2 ${
                          verificationForm.status === 'verified'
                            ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700'
                            : 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700'
                        } text-white hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
                      >
                        {verificationLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            {verificationForm.status === 'verified' ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                            {verificationForm.status === 'verified' ? 'Verify KYC' : 'Reject KYC'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-6">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/users/${submission.user_id}`)}
                className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
              >
                <span>View User Profile</span>
                <User className="w-4 h-4" />
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
              >
                <span>View Documents</span>
                <Eye className="w-4 h-4" />
              </button>
              {submission.status === 0 && (
                <>
                  <button
                    onClick={() => {
                      setVerificationForm({ status: 'verified', rejectionReason: '' });
                      setActiveTab('verify');
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
                  >
                    <span>Verify KYC</span>
                    <CheckCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setVerificationForm({ status: 'rejected', rejectionReason: '' });
                      setActiveTab('verify');
                    }}
                    className="w-full px-4 py-3 bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
                  >
                    <span>Reject KYC</span>
                    <XCircle className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                onClick={fetchSubmission}
                className="w-full px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center justify-between transition-all duration-300 hover:scale-[1.02]"
              >
                <span>Refresh Data</span>
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Submission Details */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-6">Submission Details</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Submission ID</span>
                <span className="font-mono text-sm text-white">{submission.id}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">User ID</span>
                <span className="font-medium text-white">{submission.user_id}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Document Type</span>
                <span className="font-medium text-white">{getDocumentTypeBadge(submission.document_type).props.children[2]}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Submitted</span>
                <span className="text-sm text-white">{formatDate(submission.submitted_at)}</span>
              </div>
              {submission.verified_at && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">
                    {submission.status === 1 ? 'Verified' : 'Rejected'}
                  </span>
                  <span className="text-sm text-white">{formatDate(submission.verified_at)}</span>
                </div>
              )}
              {submission.verified_by && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-300">Verified By</span>
                  <span className="text-sm text-white">{submission.verified_by}</span>
                </div>
              )}
            </div>
          </div>

          {/* Document Checklist */}
          <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-6">Document Checklist</h3>
            <div className="space-y-4">
              {documents.map((doc) => (
                <div key={doc.type} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {submission[doc.type] ? (
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      </div>
                    ) : (
                      <div className="p-2 rounded-lg bg-rose-500/20">
                        <XCircle className="w-4 h-4 text-rose-400" />
                      </div>
                    )}
                    <span className="text-sm text-white">{doc.label}</span>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full ${
                    submission[doc.type]
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-rose-500/20 text-rose-400'
                  }`}>
                    {submission[doc.type] ? 'Uploaded' : 'Missing'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-night-blue border border-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-lg font-bold text-white">Document Preview</h3>
                  <p className="text-sm text-charcoal-gray mt-1">
                    {documents.find(d => d.type === showImageModal)?.label}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => downloadDocument(showImageModal)}
                    className="p-2.5 bg-gray-800 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300 rounded-xl transition-all duration-300 hover:scale-110"
                    title="Download"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowImageModal(null)}
                    className="p-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-110"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="aspect-video rounded-lg bg-gray-800/50 flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="w-20 h-20 text-gray-700 mx-auto mb-4" />
                    <p className="text-charcoal-gray">Document preview not available</p>
                    <p className="text-sm text-gray-600 mt-2">Click download to view the file</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-center gap-3">
                  <button
                    onClick={() => downloadDocument(showImageModal)}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Document
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KYCDetail;